import React, { useMemo, useState } from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHouse,
  faBuilding,
  faIndustry,
  faFlask,
  faFileImport,
  faListCheck,
  faLink,
  faEnvelopeOpenText,
  faCode,
  faLock,
  faCalculator,
  faEuroSign,
  faClipboardCheck,
  faChartLine,
  faFileContract,
  faArrowRight,
  faCircleQuestion,
} from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { SupplierRoutePath } from 'modules/FreeSupplier/routes';
import { useSupplierDrafts } from 'modules/FreeSupplier/useSupplierDrafts';
import { SupplierFactorDraftReviewState } from 'modules/FreeSupplier/supplierDraftContracts';
import { useMockData } from '../state/MockDataContext';
import { useUpgradeModal } from './UpgradeModal';
import CbamBasicsModal from './CbamBasicsModal';

export const LITE_SIDEBAR_WIDTH = 230;

// Content + order per fixpack #15 (D-15): "Review & Drafts" moved BEFORE
// "Emissions Data" — the checklist now walks add-as-draft -> publish before
// edit/withdraw, and the nav should read in the same order the journey does.
// Paths come from the canonical SupplierRoutePath so the nav matches the
// mounted routes exactly.
// Module-scope (not exported/reused elsewhere) — built as functions of `t`
// so the nav re-renders in the active locale; called via useMemo below.
const buildFreeItems = (t) => [
  {
    label: t('freeSupplier:sidebar.gettingStarted', 'Getting Started'),
    path: SupplierRoutePath.Home,
    icon: faHouse,
    exact: true,
  },
  {
    label: t('freeSupplier:sidebar.myCompany', 'My Company'),
    path: SupplierRoutePath.Company,
    icon: faBuilding,
  },
  {
    label: t('freeSupplier:sidebar.installations', 'Installations'),
    path: SupplierRoutePath.Installations,
    icon: faIndustry,
  },
  {
    label: t('freeSupplier:sidebar.templateUpload', 'Template Upload'),
    path: SupplierRoutePath.Upload,
    icon: faFileImport,
  },
  {
    label: t('freeSupplier:sidebar.reviewAndDrafts', 'Review & Drafts'),
    path: SupplierRoutePath.Drafts,
    icon: faListCheck,
    badge: 'drafts',
  },
  {
    label: t('freeSupplier:sidebar.emissionsData', 'Emissions Data'),
    path: SupplierRoutePath.EmissionFactors,
    icon: faFlask,
  },
  {
    label: t('freeSupplier:sidebar.sharePortal', 'Share & Portal'),
    path: SupplierRoutePath.Share,
    icon: faLink,
  },
  {
    label: t('freeSupplier:sidebar.dataRequests', 'Data Requests'),
    path: SupplierRoutePath.Inbox,
    icon: faEnvelopeOpenText,
    badge: 'requests',
  },
  {
    label: t('freeSupplier:sidebar.embedWidget', 'Embed Widget'),
    path: SupplierRoutePath.Embed,
    icon: faCode,
  },
];

const buildPremiumItems = (t) => [
  {
    label: t('freeSupplier:sidebar.emissionsCalculator', 'Emissions Calculator'),
    feature: 'sefa',
    icon: faCalculator,
  },
  {
    label: t('freeSupplier:sidebar.cbamCostEstimator', 'CBAM Cost Estimator'),
    feature: 'costs',
    icon: faEuroSign,
  },
  {
    label: t('freeSupplier:sidebar.verification', 'Verification'),
    feature: 'verification',
    icon: faClipboardCheck,
  },
  {
    label: t('freeSupplier:sidebar.dataInsights', 'Data Insights'),
    feature: 'insights',
    icon: faChartLine,
  },
  {
    label: t('freeSupplier:sidebar.complianceReports', 'Compliance Reports'),
    feature: 'compliance',
    icon: faFileContract,
  },
];

// Q5 (S7 fix batch 3): the nav content itself, extracted so it can render
// identically in TWO shells — the fixed desktop `<aside>` below (`md` and up)
// and the mobile overlay drawer (`MobileNavDrawer`, `< md`) — instead of a
// second near-duplicate copy of ~100 lines of nav JSX.
export function LiteSidebarContent() {
  const { t } = useTranslation('freeSupplier');
  const { pendingRequestCount } = useMockData();
  const { openUpgrade } = useUpgradeModal();
  const history = useHistory();
  const [basicsOpen, setBasicsOpen] = useState(false);
  const freeItems = useMemo(() => buildFreeItems(t), [t]);
  const premiumItems = useMemo(() => buildPremiumItems(t), [t]);

  // Pending = proposed/under_review — the drafts still awaiting a supplier
  // decision (discarded/published are resolved and don't count).
  const draftsQuery = useSupplierDrafts();
  const pendingDraftCount = (draftsQuery.data?.items || []).filter((draft) =>
    [SupplierFactorDraftReviewState.Proposed, SupplierFactorDraftReviewState.UnderReview].includes(
      draft.reviewState,
    ),
  ).length;

  return (
    <>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2 mb-2">
          {t('sidebar.yourDataHeading', 'Your data')}
        </p>
        <ul className="list-none p-0 m-0 flex flex-col gap-0.5">
          {freeItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                exact={item.exact}
                className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-xs font-medium text-secondaryColor hover:bg-gray-50 no-underline transition-colors"
                activeClassName="!bg-primaryColor/8 !text-primaryColor ring-1 ring-primaryColor/20"
              >
                <FontAwesomeIcon
                  icon={item.icon}
                  className="w-3.5 h-3.5 shrink-0"
                />
                <span className="flex-1">{item.label}</span>
                {item.badge === 'requests' && pendingRequestCount > 0 ? (
                  <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-primaryColor text-white text-[10px] font-bold flex items-center justify-center">
                    {pendingRequestCount}
                  </span>
                ) : null}
                {item.badge === 'drafts' && pendingDraftCount > 0 ? (
                  <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-primaryColor text-white text-[10px] font-bold flex items-center justify-center">
                    {pendingDraftCount}
                  </span>
                ) : null}
              </NavLink>
            </li>
          ))}
        </ul>

        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2 mt-5 mb-2 flex items-center gap-1.5">
          {t('sidebar.premiumHeading', 'Premium')}
          <FontAwesomeIcon
            icon={faLock}
            className="text-slate-300"
          />
        </p>
        <ul className="list-none p-0 m-0 flex flex-col gap-0.5">
          {premiumItems.map((item) => (
            <li key={item.feature}>
              <button
                type="button"
                onClick={() => openUpgrade(item.feature)}
                className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-xs font-medium text-slate-400 hover:bg-gray-50 hover:text-slate-500 bg-transparent border-0 transition-colors text-left"
              >
                <FontAwesomeIcon
                  icon={item.icon}
                  className="w-3.5 h-3.5 shrink-0"
                />
                <span className="flex-1">{item.label}</span>
                <FontAwesomeIcon
                  icon={faLock}
                  className="text-[10px] text-slate-300"
                />
              </button>
            </li>
          ))}
        </ul>

        {/* Jargon help — opens the definitions & FAQ modal */}
        <button
          type="button"
          onClick={() => setBasicsOpen(true)}
          className="mt-4 w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-xs font-medium text-secondaryColor hover:bg-gray-50 bg-transparent border-0 transition-colors text-left"
        >
          <FontAwesomeIcon
            icon={faCircleQuestion}
            className="w-3.5 h-3.5 shrink-0"
          />
          {t('sidebar.cbamBasics', 'CBAM basics')}
        </button>
        <CbamBasicsModal
          open={basicsOpen}
          onOpenChange={setBasicsOpen}
        />
      </nav>

      {/* Footer upsell */}
      <div className="p-3 border-t border-slate-100">
        <div className="rounded-2xl bg-gradient-to-br from-primaryColor/15 to-primaryColor/5 border border-primaryColor/20 p-3">
          <p className="text-xs font-semibold text-slate-800 m-0">
            {t('sidebar.onFreePlanTitle', "You're on Free")}
          </p>
          <p className="text-[11px] text-slate-500 m-0 mt-1">
            {t(
              'sidebar.freeFooterBody',
              'Centralize & share your data. Unlock calculations, insights & compliance.',
            )}
          </p>
          <CgRadixButton
            variant="primary"
            size="sm"
            className="mt-2 w-full justify-center"
            onClick={() => history.push('/supplier/upgrade')}
          >
            {t('sidebar.viewPlans', 'View plans')}
            <FontAwesomeIcon
              icon={faArrowRight}
              className="text-[10px]"
            />
          </CgRadixButton>
        </div>
      </div>
    </>
  );
}

// Desktop-only fixed rail (`md` and up) — the mobile equivalent is
// `MobileNavDrawer`, rendered by `LiteShell` below `md`, wrapping the SAME
// `LiteSidebarContent`.
export default function LiteSidebar() {
  return (
    <aside
      className="hidden md:flex fixed left-0 top-14 bottom-0 bg-white/95 backdrop-blur-sm border-r border-slate-200 z-[100] flex-col"
      style={{ width: LITE_SIDEBAR_WIDTH }}
    >
      <LiteSidebarContent />
    </aside>
  );
}
